import React, { useEffect, useMemo, useState } from "react";
import { exportOrder } from "@/lib/exportOrder";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import VoterEditDialog from "@/components/VoterEditDialog";
import { VotingPersons } from "@/components/VotingPersons";
import { VotingPower } from "@/components/VotingPower";
import { DocumentDelivery } from "@/components/DocumentDelivery";
import { HolderSummary } from "@/components/HolderSummary";
import { CommitteeDocReport } from "@/components/CommitteeDocReport";
import { PersonDocSearch } from "@/components/PersonDocSearch";
import { MerkezApproval } from "@/components/MerkezApproval";
import { ASSIGNABLE_BRANCHES } from "@/lib/constants";
import { FileSignature, Search, Plus, CheckCircle2, XCircle, CalendarClock, Trash2, Loader2, Download, FileText, FileCheck2 } from "lucide-react";

const PRED_LABEL = { KESIN: "Kesin", BELKI: "Belki", GELMEYECEK: "Gelmeyecek", ULASILAMADI: "Ulaşılamadı", KARSI: "Karşı", "": "—" };

const STATUS = {
  bekliyor: { label: "Bekliyor", cls: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" },
  alindi: { label: "Alındı", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" },
  iptal: { label: "İptal", cls: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300" },
  ertelendi: { label: "Ertelendi", cls: "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
};
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function YetkiTracking() {
  const [items, setItems] = useState(null);
  const [team, setTeam] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  // create form
  const [fSearch, setFSearch] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [date, setDate] = useState(todayStr());
  const [resp, setResp] = useState("");
  const [note, setNote] = useState("");
  const [yetkiSummary, setYetkiSummary] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [editId, setEditId] = useState(null);
  // alınmış belgeler
  const [committees, setCommittees] = useState([]);
  const [doneCommittee, setDoneCommittee] = useState("all");
  const [doneTab, setDoneTab] = useState(() => (
    new URLSearchParams(window.location.search).get("tab") === "merkez" ? "merkez" : "done"
  ));
  const [merkezCount, setMerkezCount] = useState(0);
  useEffect(() => {
    const load = () => api.get("/yetki/merkez-pending")
      .then(({ data }) => setMerkezCount(data.total || 0)).catch(() => {});
    load();
    const onChanged = () => load();
    window.addEventListener("merkez-pending-changed", onChanged);
    return () => window.removeEventListener("merkez-pending-changed", onChanged);
  }, []);
  const [doneSearch, setDoneSearch] = useState("");
  const [doneList, setDoneList] = useState(null);
  const [doneExporting, setDoneExporting] = useState(false);
  const [ratioSort, setRatioSort] = useState("ratio");
  const [ratioDir, setRatioDir] = useState("desc");
  const [ratioExporting, setRatioExporting] = useState(false);
  const [apExporting, setApExporting] = useState(false);
  const [holderLoc, setHolderLoc] = useState("all");
  const [holderBranch, setHolderBranch] = useState("all");
  const [holderRefresh, setHolderRefresh] = useState(0);
  const [pageCommittee, setPageCommittee] = useState("all");

  useEffect(() => { setDoneCommittee(pageCommittee); }, [pageCommittee]);

  const blobDownload = (data, name) => {
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const exportRatio = async (fmt) => {
    setRatioExporting(true);
    try {
      const params = { format: fmt, only: 1, order: await exportOrder(ratioT.sorted.map((r) => r.committee_no)) };
      const res = await api.get("/reports/yetki-ratio/export", { params, responseType: "blob" });
      blobDownload(res.data, `yetki_belgesi_oran.${fmt === "pdf" ? "pdf" : "xlsx"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dışa aktarma başarısız");
    } finally { setRatioExporting(false); }
  };

  const exportAppts = async (fmt) => {
    setApExporting(true);
    try {
      const params = { format: fmt, only: 1, order: await exportOrder(apT.sorted.map((r) => r.id)) };
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/yetki-appointments/export", { params, responseType: "blob" });
      blobDownload(res.data, `yetki_randevulari.${fmt === "pdf" ? "pdf" : "xlsx"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dışa aktarma başarısız");
    } finally { setApExporting(false); }
  };

  const ratioPct = (r) => (r.belge_target ? Math.round((r.belge || 0) / r.belge_target * 100) : ((r.belge || 0) ? 100 : 0));

  const ratioRows = useMemo(() => {
    const list = (yetkiSummary || [])
      .filter((r) => (r.kesin_faal || 0) > 0 || (r.belge || 0) > 0)
      .filter((r) => pageCommittee === "all" || r.committee_no === Number(pageCommittee));
    if (ratioSort === "committee") return list.slice().sort((a, b) => a.committee_no - b.committee_no);
    const val = (r) => (ratioSort === "ratio" ? ratioPct(r) : ratioSort === "belge" ? (r.belge || 0) : (r.kesin_faal || 0));
    return list.slice().sort((a, b) => (ratioDir === "asc" ? val(a) - val(b) : val(b) - val(a)));
  }, [yetkiSummary, ratioSort, ratioDir, pageCommittee]);

  const loadDone = () => {
    if (doneTab === "persons" || doneTab === "delivery" || doneTab === "guc") return Promise.resolve();
    setDoneList(null);
    const params = doneTab === "done"
      ? { proxy: "yes", entity: "tuzel", ...(holderLoc !== "all" ? { holder: holderLoc } : {}),
          ...(holderLoc === "sube" && holderBranch !== "all" ? { holder_branch: holderBranch } : {}) }
      : { proxy: "no", prediction: "KESIN", status: "faal", entity: "tuzel" };
    if (doneCommittee !== "all") params.committee_no = Number(doneCommittee);
    if (doneSearch.trim().length >= 2) params.search = doneSearch.trim();
    return api.get("/voters", { params })
      .then(({ data }) => setDoneList(data || []))
      .catch(() => { setDoneList([]); toast.error("Liste yüklenemedi"); });
  };

  const exportDone = async (fmt) => {
    setDoneExporting(true);
    try {
      const params = {
        format: fmt,
        order: await exportOrder(doneT.sorted.map((r) => r.id)),
        columns: "sicil_no,company_name,committee_no,committee_name,contact_person,branch,status,current_vote_prediction_2026,team_assignment_2026,proxy_document,proxy_document_date,document_holder,voting_person",
        ...(doneTab === "done"
          ? { proxy: "yes", entity: "tuzel", ...(holderLoc !== "all" ? { holder: holderLoc } : {}),
              ...(holderLoc === "sube" && holderBranch !== "all" ? { holder_branch: holderBranch } : {}) }
          : { proxy: "no", prediction: "KESIN", status: "faal", entity: "tuzel" }),
      };
      if (doneCommittee !== "all") params.committee_no = Number(doneCommittee);
      if (doneSearch.trim().length >= 2) params.search = doneSearch.trim();
      const res = await api.get("/voters/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doneTab === "done" ? "alinmis" : "eksik"}_yetki_belgeleri.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dışa aktarma başarısız");
    } finally {
      setDoneExporting(false);
    }
  };

  const doneRows = useMemo(() => {
    const list = (doneList || []).slice();
    if (doneTab === "done") {
      list.sort((a, b) => (b.proxy_document_date || "").localeCompare(a.proxy_document_date || "")
        || (a.company_name || "").localeCompare(b.company_name || "", "tr"));
    }
    return list;
  }, [doneList, doneTab]);
  const ratioT = useTableSort(ratioRows);
  const doneT = useTableSort(doneRows);
  const [selIds, setSelIds] = useState([]);
  const [bulkHolder, setBulkHolder] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const holderOptions = ["Seçim Ofisi", ...ASSIGNABLE_BRANCHES.map((b) => `${b} Şube`), "Kendi Aldı"];

  const applyBulkHolder = async () => {
    if (!bulkHolder || selIds.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/reports/document-holders/bulk", { ids: selIds, holder: bulkHolder });
      toast.success(`${data.updated} firmanın belge yeri "${data.holder}" olarak işaretlendi`);
      setSelIds([]);
      setBulkHolder("");
      setHolderRefresh((n) => n + 1);
      loadDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toplu işaretleme başarısız");
    } finally { setBulkBusy(false); }
  };


  const load = () => {
    const params = statusFilter !== "all" ? { status: statusFilter } : {};
    return api.get("/yetki-appointments", { params }).then(({ data }) => setItems(data)).catch(() => toast.error("Randevular yüklenemedi"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { api.get("/meta").then(({ data }) => { setTeam(data.team_members || []); setSectors(data.sectors || []); }).catch(() => {}); }, []);
  useEffect(() => { api.get("/committees").then(({ data }) => setCommittees(data || [])).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(loadDone, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [doneCommittee, doneSearch, doneTab, holderLoc, holderBranch]);
  useEffect(() => { api.get("/dashboard/committee-analytics").then(({ data }) => setYetkiSummary(data.committees || [])).catch(() => setYetkiSummary([])); }, []);

  const searchFirms = async (term) => {
    setFSearch(term);
    if (term.trim().length < 2) { setResults([]); return; }
    try {
      const { data } = await api.get("/voters", { params: { search: term, page_size: 8 } });
      setResults((data || []).slice(0, 8));
    } catch { setResults([]); }
  };

  const create = async () => {
    if (!picked || !date) { toast.error("Firma ve tarih seçin"); return; }
    setBusy(true);
    try {
      await api.post("/yetki-appointments", { voter_id: picked.id, date, responsible: resp || null, note });
      toast.success("Randevu oluşturuldu");
      setPicked(null); setFSearch(""); setResults([]); setResp(""); setNote(""); setDate(todayStr());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Oluşturulamadı");
    } finally { setBusy(false); }
  };

  const setStatus = async (a, status) => {
    try { await api.patch(`/yetki-appointments/${a.id}`, { status }); toast.success(`Durum: ${STATUS[status].label}`); load(); }
    catch { toast.error("Güncellenemedi"); }
  };
  const del = async (a) => {
    try { await api.delete(`/yetki-appointments/${a.id}`); toast.success("Randevu silindi"); load(); }
    catch { toast.error("Silinemedi"); }
  };

  const sorted = useMemo(() => (items || [])
    .filter((a) => pageCommittee === "all" || a.committee_no === Number(pageCommittee))
    .slice().sort((x, y) => (x.date || "").localeCompare(y.date || "")), [items, pageCommittee]);
  const apT = useTableSort(sorted);
  const yTotals = useMemo(() => {
    const list = (yetkiSummary || []).filter((r) => pageCommittee === "all" || r.committee_no === Number(pageCommittee));
    const belge = list.reduce((a, r) => a + (r.belge || 0), 0);
    const target = list.reduce((a, r) => a + (r.belge_target || 0), 0);
    const behind = list.filter((r) => (r.belge_target || 0) > 0).sort((a, b) => (a.belge_pct || 0) - (b.belge_pct || 0)).slice(0, 6);
    return { belge, target, pct: target ? Math.round((belge / target) * 100) : 0, behind };
  }, [yetkiSummary, pageCommittee]);
  const t = todayStr();
  const overdue = (a) => a.status === "bekliyor" && a.date < t;
  const isToday = (a) => a.date === t;

  return (
    <div className="space-y-6" data-testid="yetki-tracking-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl grid place-items-center bg-[#0F2C59]/10 text-[#0F2C59]">
          <FileSignature className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#0F2C59] dark:text-sky-300">Yetki Belgesi Takip</h1>
          <p className="text-sm text-muted-foreground">Firma randevuları — tarihinde yetki belgesi dilekçesi alınacak</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground hidden sm:inline">Komite:</span>
          <Select value={pageCommittee} onValueChange={setPageCommittee}>
            <SelectTrigger className="h-9 w-[250px]" data-testid="yetki-page-committee"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="all">Tüm komitelerim</SelectItem>
              {committees.map((c) => (
                <SelectItem key={c.committee_no} value={String(c.committee_no)}>#{c.committee_no} · {c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {yetkiSummary && yetkiSummary.length > 0 && (
        <Card className="p-4 space-y-3" data-testid="yetki-target-summary">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-medium">Yetki Belgesi Hedefi <span className="text-xs text-muted-foreground font-normal">(her komite KESİN oyunun %60'ı kadar toplamalı)</span></p>
            <span className="text-sm font-bold text-[#0F2C59] dark:text-sky-300" data-testid="yetki-target-total">{yTotals.belge} / {yTotals.target} · %{yTotals.pct}</span>
          </div>
          <div className="h-2.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-[#0F2C59] dark:bg-sky-400" style={{ width: `${Math.min(yTotals.pct, 100)}%` }} />
          </div>
          {yTotals.behind.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap text-xs">
              <span className="text-red-600 font-semibold">En geride:</span>
              {yTotals.behind.map((r) => (
                <span key={r.committee_no} className="px-2 py-1 rounded-md bg-red-500/10 text-red-600 border border-red-500/30" title={r.name} data-testid={`yetki-behind-${r.committee_no}`}>#{r.committee_no} · {r.belge}/{r.belge_target} (%{r.belge_pct})</span>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Komite komite: belge / kesin oy oranı */}
      {Array.isArray(yetkiSummary) && yetkiSummary.length > 0 && (
        <Card className="p-4 space-y-3" data-testid="yetki-ratio-card">
          <div className="flex items-center gap-2 flex-wrap">
            <FileSignature className="h-4 w-4 text-[#0F2C59] dark:text-sky-300" />
            <p className="text-sm font-medium">Komite Komite · Yetki Belgesi / Hedef Oranı</p>
            <span className="text-xs text-muted-foreground">— FAAL firmalar; hedef: kesin oyun %60'ı</span>
            <div className="ml-auto flex items-center gap-2">
              <Select value={ratioSort} onValueChange={setRatioSort}>
                <SelectTrigger className="h-9 w-[210px]" data-testid="yetki-ratio-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ratio">Sırala: Belge/Hedef Oranı</SelectItem>
                  <SelectItem value="belge">Sırala: Alınan Belge</SelectItem>
                  <SelectItem value="kesin">Sırala: Kesin Oy</SelectItem>
                  <SelectItem value="committee">Sırala: Komite No</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-9" disabled={ratioSort === "committee"}
                onClick={() => setRatioDir((d) => (d === "desc" ? "asc" : "desc"))} data-testid="yetki-ratio-dir">
                {ratioDir === "desc" ? "İyiden Kötüye" : "Kötüden İyiye"}
              </Button>
              <Button variant="outline" size="sm" className="h-9" disabled={ratioExporting} onClick={() => exportRatio("xlsx")} data-testid="yetki-ratio-export-xlsx">
                <Download className="h-4 w-4 mr-1.5" />Excel
              </Button>
              <Button variant="outline" size="sm" className="h-9" disabled={ratioExporting} onClick={() => exportRatio("pdf")} data-testid="yetki-ratio-export-pdf">
                <FileText className="h-4 w-4 mr-1.5" />PDF
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto rounded-md border border-border">
            <table className="w-full text-sm" data-testid="yetki-ratio-table">
              <thead className="sticky top-0 bg-muted/90 backdrop-blur">
                <tr className="text-left text-xs uppercase text-muted-foreground">
                  <ratioT.Th sortKey="yr-komite" get={(r) => r.name} text className="px-3 py-2 font-medium">Komite</ratioT.Th>
                  <ratioT.Th sortKey="yr-kesin" get={(r) => r.kesin_faal ?? 0} className="px-3 py-2 font-medium text-right">Kesin Oy</ratioT.Th>
                  <ratioT.Th sortKey="yr-belge" get={(r) => r.belge ?? 0} className="px-3 py-2 font-medium text-right">Alınan Belge</ratioT.Th>
                  <ratioT.Th sortKey="yr-hedef" get={(r) => r.belge_target ?? 0} className="px-3 py-2 font-medium text-right">Hedef (%60)</ratioT.Th>
                  <ratioT.Th sortKey="yr-oran" get={(r) => ratioPct(r)} className="px-3 py-2 font-medium w-56">Belge / Hedef Oy</ratioT.Th>
                </tr>
              </thead>
              <tbody>
                {ratioT.sorted.map((r, i) => (
                  <tr key={r.committee_no} className="border-t border-border/60" data-testid={`yetki-ratio-row-${r.committee_no}`}>
                    <td className="px-3 py-2">
                      {ratioSort !== "committee" && <span className="inline-flex items-center justify-center h-5 min-w-[1.35rem] px-1 mr-1.5 rounded bg-muted text-[11px] font-bold text-muted-foreground">{i + 1}</span>}
                      <span className="text-muted-foreground">#{r.committee_no}</span> <span className="font-medium">{r.name}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.kesin_faal ?? 0}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-600">{r.belge ?? 0}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.belge_target ?? 0}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                          <div className="h-full" style={{ width: `${Math.min(ratioPct(r), 100)}%`, background: ratioPct(r) >= 100 ? "#16A34A" : ratioPct(r) >= 60 ? "#D97706" : "#DC2626" }} />
                        </div>
                        <span className="tabular-nums text-xs w-12 text-right font-semibold" data-testid={`yetki-ratio-pct-${r.committee_no}`}>%{ratioPct(r)}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Alınmış yetki belgeleri */}
      <Card className="p-4 space-y-3" data-testid="yetki-done-card">
        <div className="flex items-center gap-2 flex-wrap">
          <FileCheck2 className="h-4 w-4 text-emerald-600 shrink-0" />
          <p className="text-sm font-medium">Yetki Belgesi Durumu</p>
          <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="yetki-done-tabs">
            <button type="button" onClick={() => setDoneTab("done")} className={`px-3 py-1.5 text-xs font-medium ${doneTab === "done" ? "bg-emerald-600 text-white" : "hover:bg-muted"}`} data-testid="yetki-tab-done">Alınmış</button>
            <button type="button" onClick={() => setDoneTab("missing")} className={`px-3 py-1.5 text-xs font-medium ${doneTab === "missing" ? "bg-red-600 text-white" : "hover:bg-muted"}`} data-testid="yetki-tab-missing">Eksik (Kesin oy)</button>
            <button type="button" onClick={() => setDoneTab("persons")} className={`px-3 py-1.5 text-xs font-medium ${doneTab === "persons" ? "bg-[#0F2C59] text-white" : "hover:bg-muted"}`} data-testid="yetki-tab-persons">Oy Kullanacak Yetkililer</button>
            <button type="button" onClick={() => setDoneTab("guc")} className={`px-3 py-1.5 text-xs font-medium ${doneTab === "guc" ? "bg-[#D4AF37] text-white" : "hover:bg-muted"}`} data-testid="yetki-tab-guc">Oy Gücü Sıralaması</button>
            <button type="button" onClick={() => setDoneTab("delivery")} className={`px-3 py-1.5 text-xs font-medium ${doneTab === "delivery" ? "bg-violet-600 text-white" : "hover:bg-muted"}`} data-testid="yetki-tab-delivery">Belge Teslim Takibi</button>
            <button type="button" onClick={() => setDoneTab("rapor")} className={`px-3 py-1.5 text-xs font-medium ${doneTab === "rapor" ? "bg-[#0F2C59] text-white" : "hover:bg-muted"}`} data-testid="yetki-tab-rapor">Komite Belge Raporu</button>
            <button type="button" onClick={() => setDoneTab("isim")} className={`px-3 py-1.5 text-xs font-medium ${doneTab === "isim" ? "bg-cyan-600 text-white" : "hover:bg-muted"}`} data-testid="yetki-tab-isim">İsim Bazlı Arama</button>
            <button type="button" onClick={() => setDoneTab("merkez")} className={`px-3 py-1.5 text-xs font-medium ${doneTab === "merkez" ? "bg-amber-600 text-white" : "hover:bg-muted"}`} data-testid="yetki-tab-merkez">Seçim Ofisi Belge Onayı
              {merkezCount > 0 && (
                <span className="ml-1.5 inline-flex min-w-[18px] h-[18px] px-1 items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="yetki-tab-merkez-badge">
                  {merkezCount > 99 ? "99+" : merkezCount}
                </span>
              )}
            </button>
          </div>
          <span className="text-xs text-muted-foreground">
            {doneTab === "done" ? "— firma kaydında “Yetki Belgesi: Alındı” işaretli firmalar"
              : doneTab === "missing" ? "— 2026 tahmini KESİN, FAAL ve belgesi alınmamış firmalar"
                : doneTab === "isim" ? "— isim yazın: o kişinin kaç oyu var, hangi komitelerde, belgeler kimde"
                : doneTab === "guc" ? "— en çok oy kullanacak kişiden en aza doğru sıralama (komite komite filtrelenebilir)"
                : doneTab === "merkez" ? "— “Seçim Ofisine dilekçesi verildi” işaretlenip ofis kontrolü bekleyen belgeler"
                : doneTab === "rapor" ? "— KESİN işaretli tüm firmalar: belge alındı mı, belge kimde, oy kullanacak yetkili kim"
                  : doneTab === "delivery" ? "— belge kimde, sandığa kim götürecek: adım adım teslim zinciri"
                    : "— belgesi alınan firmalarda sandığa gelecek yetkililer"}
          </span>
          {doneTab !== "persons" && doneTab !== "guc" && doneTab !== "delivery" && doneTab !== "rapor" && doneTab !== "isim" && doneTab !== "merkez" && (
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <Select value={doneCommittee} onValueChange={setDoneCommittee}>
              <SelectTrigger className="h-9 w-[230px]" data-testid="yetki-done-committee"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Tüm komitelerim</SelectItem>
                {committees.map((c) => (
                  <SelectItem key={c.committee_no} value={String(c.committee_no)}>#{c.committee_no} · {c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input className="pl-8 h-9 w-[190px]" placeholder="Firma ara..." value={doneSearch} onChange={(e) => setDoneSearch(e.target.value)} data-testid="yetki-done-search" />
            </div>
            <Button variant="outline" size="sm" className="h-9" disabled={doneExporting || !(doneList || []).length} onClick={() => exportDone("xlsx")} data-testid="yetki-done-export-xlsx"><Download className="h-4 w-4 mr-1.5" />Excel</Button>
            <Button variant="outline" size="sm" className="h-9" disabled={doneExporting || !(doneList || []).length} onClick={() => exportDone("pdf")} data-testid="yetki-done-export-pdf"><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
          </div>
          )}
        </div>

        {doneTab === "done" && (
          <HolderSummary committeeNo={doneCommittee} location={holderLoc} branch={holderBranch}
            refresh={holderRefresh}
            onLocationChange={setHolderLoc} onBranchChange={setHolderBranch} />
        )}

        {doneTab === "persons" ? (
          <VotingPersons committeeNo={pageCommittee} />
        ) : doneTab === "guc" ? (
          <VotingPower committeeNo={pageCommittee} />
        ) : doneTab === "isim" ? (
          <PersonDocSearch />
        ) : doneTab === "merkez" ? (
          <MerkezApproval />
        ) : doneTab === "rapor" ? (
          <CommitteeDocReport committeeNo={pageCommittee} />
        ) : doneTab === "delivery" ? (
          <DocumentDelivery committeeNo={pageCommittee} />
        ) : !doneList ? (
          <Skeleton className="h-40 w-full" />
        ) : doneRows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="yetki-done-empty">
            {doneTab === "done" ? "Bu kapsamda alınmış yetki belgesi bulunamadı." : "Bu kapsamda belgesi eksik KESİN oy bulunamadı."}
          </p>
        ) : (
          <>
            <p className="text-sm" data-testid="yetki-done-count">
              <span className={`font-bold ${doneTab === "done" ? "text-emerald-600" : "text-red-600"}`}>{doneTab === "done" ? doneRows.filter((v) => v.status === "faal").length : doneRows.length}</span>
              {doneTab === "done" ? " alınmış belge (FAAL)" : " belgesi eksik KESİN oy"}
              {doneTab === "done" && doneRows.some((v) => v.status !== "faal") && (
                <span className="text-muted-foreground"> · {doneRows.filter((v) => v.status !== "faal").length} ASKI hariç</span>
              )}
            </p>
            {doneTab === "done" && selIds.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap rounded-md border border-cyan-500/40 bg-cyan-50 dark:bg-cyan-950/30 p-2.5" data-testid="holder-bulk-bar">
                <span className="text-sm font-medium">{selIds.length} firma seçildi — belge yerini işaretle:</span>
                <Select value={bulkHolder} onValueChange={setBulkHolder}>
                  <SelectTrigger className="h-9 w-[220px]" data-testid="holder-bulk-select"><SelectValue placeholder="Belge yeri seç" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {holderOptions.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" className="h-9 bg-[#0F2C59] hover:bg-[#0F2C59]/90" disabled={!bulkHolder || bulkBusy}
                  onClick={applyBulkHolder} data-testid="holder-bulk-apply">
                  {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "İşaretle"}
                </Button>
                <Button size="sm" variant="ghost" className="h-9" onClick={() => setSelIds([])} data-testid="holder-bulk-clear">Vazgeç</Button>
              </div>
            )}
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto rounded-md border border-border">
              <table className="w-full text-sm" data-testid="yetki-done-table">
                <thead className="sticky top-0 bg-muted/90 backdrop-blur">
                  <tr className="text-left text-xs uppercase text-muted-foreground">
                    {doneTab === "done" && (
                      <th className="px-3 py-2 w-8">
                        <input type="checkbox" className="h-4 w-4 accent-[#0F2C59]" data-testid="yetki-sel-all"
                          checked={doneT.sorted.length > 0 && selIds.length === doneT.sorted.length}
                          onChange={(e) => setSelIds(e.target.checked ? doneT.sorted.map((r) => r.id) : [])} />
                      </th>
                    )}
                    <doneT.Th sortKey="yd-firma" get={(r) => r.company_name} text className="px-3 py-2 font-medium">Firma</doneT.Th>
                    <doneT.Th sortKey="yd-komite" get={(r) => r.committee_no} className="px-3 py-2 font-medium">Komite / Şube</doneT.Th>
                    <doneT.Th sortKey="yd-sorumlu" get={(r) => r.team_assignment_2026} text className="px-3 py-2 font-medium">Sorumlu</doneT.Th>
                    <doneT.Th sortKey="yd-durum" get={(r) => r.status} text className="px-3 py-2 font-medium">Durum</doneT.Th>
                    <doneT.Th sortKey="yd-tahmin" get={(r) => r.current_vote_prediction_2026 || ""} text className="px-3 py-2 font-medium">2026 Tahmin</doneT.Th>
                    {doneTab === "done" && <doneT.Th sortKey="yd-holder" get={(r) => r.document_holder || ""} text className="px-3 py-2 font-medium">Belge Kimde / Nerede</doneT.Th>}
                    {doneTab === "done" && <doneT.Th sortKey="yd-tarih" get={(r) => r.proxy_document_date} text className="px-3 py-2 font-medium">Belge Tarihi</doneT.Th>}
                  </tr>
                </thead>
                <tbody>
                  {doneT.sorted.map((v) => (
                    <tr key={v.id} className="border-t border-border/60" data-testid={`yetki-done-row-${v.id}`}>
                      {doneTab === "done" && (
                        <td className="px-3 py-2">
                          <input type="checkbox" className="h-4 w-4 accent-[#0F2C59]" data-testid={`yetki-sel-${v.id}`}
                            checked={selIds.includes(v.id)}
                            onChange={(e) => setSelIds((prev) => e.target.checked ? [...prev, v.id] : prev.filter((x) => x !== v.id))} />
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <button type="button" className="font-medium text-left hover:underline" onClick={() => setEditId(v.id)} data-testid={`yetki-done-firm-${v.id}`}>{v.company_name}</button>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">#{v.committee_no} · {v.branch}</td>
                      <td className="px-3 py-2 text-xs">{v.team_assignment_2026 || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2 text-xs">{v.status === "faal" ? "FAAL" : "ASKI"}</td>
                      <td className="px-3 py-2 text-xs">{PRED_LABEL[v.current_vote_prediction_2026 || ""] ?? v.current_vote_prediction_2026}</td>
                      {doneTab === "done" && (
                        <td className="px-3 py-2 text-xs" data-testid={`yetki-done-holder-${v.id}`}>
                          {v.document_holder || <span className="text-muted-foreground">—</span>}
                        </td>
                      )}
                      {doneTab === "done" && (
                        <td className="px-3 py-2 text-xs whitespace-nowrap" data-testid={`yetki-done-date-${v.id}`}>
                          {v.proxy_document_date || <span className="text-muted-foreground">—</span>}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {/* Create */}
      <Card className="p-4 space-y-3" data-testid="yetki-create-card">
        <p className="text-sm font-medium">Yeni Randevu</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Firma ara (isim/sicil)..." value={picked ? picked.company_name : fSearch}
              onChange={(e) => { setPicked(null); searchFirms(e.target.value); }} data-testid="yetki-firm-search" />
            {!picked && results.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-64 overflow-auto">
                {results.map((v) => (
                  <button key={v.id} onClick={() => { setPicked(v); setResults([]); }} className="block w-full text-left px-3 py-2 hover:bg-muted text-sm" data-testid={`yetki-firm-opt-${v.id}`}>
                    <span className="font-medium">{v.company_name}</span>
                    <span className="text-xs text-muted-foreground"> · #{v.committee_no} · {v.branch}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="yetki-date" />
          <Select value={resp || "none"} onValueChange={(v) => setResp(v === "none" ? "" : v)}>
            <SelectTrigger data-testid="yetki-responsible"><SelectValue placeholder="Sorumlu" /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="none">Sorumlu yok</SelectItem>
              {team.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3">
          <Input placeholder="Not (opsiyonel)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="yetki-note" className="flex-1" />
          <Button onClick={create} disabled={busy || !picked} className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="yetki-create-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" />Ekle</>}
          </Button>
        </div>
      </Card>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground">Durum:</span>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="yetki-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="bekliyor">Bekliyor</SelectItem>
            <SelectItem value="alindi">Alındı</SelectItem>
            <SelectItem value="ertelendi">Ertelendi</SelectItem>
            <SelectItem value="iptal">İptal</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-9" disabled={apExporting || !sorted.length} onClick={() => exportAppts("xlsx")} data-testid="yetki-appt-export-xlsx">
            <Download className="h-4 w-4 mr-1.5" />Excel
          </Button>
          <Button variant="outline" size="sm" className="h-9" disabled={apExporting || !sorted.length} onClick={() => exportAppts("pdf")} data-testid="yetki-appt-export-pdf">
            <FileText className="h-4 w-4 mr-1.5" />PDF
          </Button>
        </div>
      </div>

      {!items ? (
        <Skeleton className="h-72 w-full" />
      ) : sorted.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="yetki-empty">Randevu bulunamadı.</Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="yetki-table">
              <thead>
                <tr className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                  <apT.Th sortKey="ap-tarih" get={(r) => r.date} text className="px-4 py-3 font-medium">Tarih</apT.Th>
                  <apT.Th sortKey="ap-firma" get={(r) => r.company_name} text className="px-4 py-3 font-medium">Firma</apT.Th>
                  <apT.Th sortKey="ap-komite" get={(r) => r.committee_no} className="px-4 py-3 font-medium">Komite / Şube</apT.Th>
                  <apT.Th sortKey="ap-sorumlu" get={(r) => r.responsible || r.team_assignment_2026} text className="px-4 py-3 font-medium">Sorumlu</apT.Th>
                  <apT.Th sortKey="ap-durum" get={(r) => r.status} text className="px-4 py-3 font-medium">Durum</apT.Th>
                  <th className="px-4 py-3 font-medium text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {apT.sorted.map((a) => (
                  <tr key={a.id} className={`border-t border-border/60 ${overdue(a) ? "bg-red-50 dark:bg-red-950/30" : isToday(a) ? "bg-amber-50 dark:bg-amber-950/20" : ""}`} data-testid={`yetki-row-${a.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap font-medium">
                      {a.date}
                      {overdue(a) && <span className="ml-1 text-[10px] text-red-600 font-semibold">gecikmiş</span>}
                      {isToday(a) && <span className="ml-1 text-[10px] text-amber-600 font-semibold">bugün</span>}
                    </td>
                    <td className="px-4 py-3">{a.company_name}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">#{a.committee_no} · {a.branch}</td>
                    <td className="px-4 py-3">{a.responsible || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS[a.status]?.cls}`}>{STATUS[a.status]?.label}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {a.status !== "alindi" && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" title="Alındı" onClick={() => setStatus(a, "alindi")} data-testid={`yetki-done-${a.id}`}><CheckCircle2 className="h-4 w-4" /></Button>
                        )}
                        {a.status === "bekliyor" && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500" title="Ertele" onClick={() => setStatus(a, "ertelendi")} data-testid={`yetki-postpone-${a.id}`}><CalendarClock className="h-4 w-4" /></Button>
                        )}
                        {a.status !== "iptal" && a.status !== "alindi" && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" title="İptal" onClick={() => setStatus(a, "iptal")} data-testid={`yetki-cancel-${a.id}`}><XCircle className="h-4 w-4" /></Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Sil" onClick={() => del(a)} data-testid={`yetki-del-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <VoterEditDialog voterId={editId} open={!!editId} onClose={() => setEditId(null)} onSaved={() => { loadDone(); load(); }} team={team} sectors={sectors} />
    </div>
  );
}
